import React, { createContext, useContext } from 'react';
import { Box, Stack, Typography, Paper } from '@mui/material';
import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts';
import dayjs from '@/config/dayjs';
import {
  AssetAllocationDataRow,
  TOOLTIP_DATE_FORMAT,
} from './assetAllocationChartUtils';

export const ChartDataContext = createContext<AssetAllocationDataRow[]>([]);

function AxisTooltipContentSorted() {
  const dataSet = useContext(ChartDataContext);
  const axesData = useAxesTooltip();

  if (!axesData?.length) {
    return (
      <Paper
        elevation={3}
        sx={{ p: 1, pointerEvents: 'none' }}
      >
        <Typography variant='caption'>&nbsp;</Typography>
      </Paper>
    );
  }

  return (
    <>
      {axesData.map(({ axisFormattedValue, seriesItems, dataIndex }) => {
        const sorted = [...seriesItems].sort(
          (a, b) => Number(b.value) - Number(a.value),
        );
        const row = dataSet[dataIndex];
        const exactDate =
          row?.date instanceof Date
            ? dayjs(row.date).format(TOOLTIP_DATE_FORMAT)
            : axisFormattedValue;

        return (
          <Paper
            key={String(axisFormattedValue)}
            elevation={3}
            sx={{ p: 1.5, pointerEvents: 'none' }}
          >
            <Typography
              variant='subtitle2'
              sx={{ fontWeight: 600 }}
            >
              {exactDate}
            </Typography>
            <Stack
              component='ul'
              sx={{ listStyle: 'none', pl: 0, m: 0, mt: 0.5 }}
            >
              {sorted.map((item) => (
                <Stack
                  component='li'
                  key={item.seriesId}
                  direction='row'
                  alignItems='center'
                  gap={1}
                  sx={{ py: 0.25 }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: item.color,
                    }}
                  />
                  <Typography
                    variant='body2'
                    sx={{ flex: 1 }}
                  >
                    {item.formattedLabel ?? item.seriesId}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 500 }}
                  >
                    {item.formattedValue}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>
        );
      })}
    </>
  );
}

export function AssetAllocationChartTooltip(
  props: React.ComponentProps<typeof ChartsTooltipContainer>,
) {
  return (
    <ChartsTooltipContainer
      {...props}
      trigger='axis'
    >
      <AxisTooltipContentSorted />
    </ChartsTooltipContainer>
  );
}
